// Pure DOM decorator for diagram-js/bpmnlint's raw error/warning tooltips (`.djs-tooltip-error`
// / `.djs-tooltip-warning`). Rebuilds each tip's markup into a themable icon+text pair and closes
// it on interaction. Colours come entirely from CSS (`.kuika-runtime-lint-tip` /
// `--warning` in the SCSS) — this module never sets an inline colour and never injects a
// <style> tag, so it stays dark-mode compliant without duplicating tokens in JS.

const decoratedTips: WeakSet<HTMLElement> = new WeakSet<HTMLElement>();

export const RUNTIME_LINT_TIP_CLASS: string = "kuika-runtime-lint-tip";
export const RUNTIME_LINT_TIP_WARNING_MODIFIER_CLASS: string = "kuika-runtime-lint-tip--warning";
export const RUNTIME_LINT_TIP_ICON_CLASS: string = "kuika-runtime-lint-tip-icon";
export const RUNTIME_LINT_TIP_TEXT_CLASS: string = "kuika-runtime-lint-tip-text";

export const normalizeLintTipText = (text: string): string =>
  (text || "")
    .replace(/\s+/g, " ")
    .replace(/^×\s*/g, "")
    .trim();

const closeRuntimeLintTip = (tip: HTMLElement): void => {
  try {
    tip.style.display = "none";
    tip.remove();
  } catch {
    // tip may already be detached from the DOM; nothing further to clean up.
  }
};

export const decorateRuntimeLintTip = (tip: HTMLElement): void => {
  const classNameValue: string = typeof tip.className === "string" ? tip.className : "";
  const isError: boolean = classNameValue.includes("djs-tooltip-error");
  const isWarning: boolean = classNameValue.includes("djs-tooltip-warning");
  if (!isError && !isWarning) return;

  const existingTextEl: HTMLElement | null = tip.querySelector(`.${RUNTIME_LINT_TIP_TEXT_CLASS}`);
  const rawMessage: string = existingTextEl ? existingTextEl.textContent || "" : tip.textContent || "";
  const message: string = normalizeLintTipText(rawMessage);
  if (!message) return;

  tip.classList.add(RUNTIME_LINT_TIP_CLASS);
  tip.classList.toggle(RUNTIME_LINT_TIP_WARNING_MODIFIER_CLASS, isWarning);

  // Listeners are keyed only on the WeakSet, never on whether the icon/text markup below is
  // still present: diagram-js/bpmnlint can rewrite the tip's raw content on a later lint pass
  // (wiping our spans) without ever replacing the tip element itself, so re-attaching here would
  // silently duplicate the close handlers on the same node.
  if (!decoratedTips.has(tip)) {
    tip.addEventListener("mouseenter", () => closeRuntimeLintTip(tip));
    tip.addEventListener("click", () => closeRuntimeLintTip(tip));
    decoratedTips.add(tip);
  }

  const hasIcon: boolean = tip.querySelector(`.${RUNTIME_LINT_TIP_ICON_CLASS}`) !== null;
  if (!hasIcon) {
    tip.textContent = "";

    const icon: HTMLSpanElement = document.createElement("span");
    icon.className = RUNTIME_LINT_TIP_ICON_CLASS;
    icon.textContent = "×";
    tip.appendChild(icon);

    const textSpan: HTMLSpanElement = document.createElement("span");
    textSpan.className = RUNTIME_LINT_TIP_TEXT_CLASS;
    tip.appendChild(textSpan);
  }

  // Never innerHTML with `message` — it is untrusted diagram text (a lint rule's raw
  // description string). textContent renders it verbatim, never as markup.
  const textEl: HTMLElement | null = tip.querySelector(`.${RUNTIME_LINT_TIP_TEXT_CLASS}`);
  if (textEl) textEl.textContent = message;
};
